import { decodeBase64Url } from "./base64";
import { detectHomoglyph, extractHostname, extractETldPlusOne, getDomainFlags, looksLikeShortener } from "./domainUtils";
import { ExtractedLink, GmailPayloadPart } from "./types";

const URL_REGEX = /(https?:\/\/[^\s"<>()]+)/gi;

function collectParts(part?: GmailPayloadPart): GmailPayloadPart[] {
  if (!part) return [];
  if (!part.parts?.length) return [part];
  return [part, ...part.parts.flatMap((p) => collectParts(p))];
}

function decodeBody(body?: { data?: string }): string {
  if (!body?.data) return "";
  return decodeBase64Url(body.data);
}

export function extractLinksFromPayload(payload?: GmailPayloadPart): ExtractedLink[] {
  const parts = collectParts(payload);
  const links: ExtractedLink[] = [];

  for (const part of parts) {
    if (part.mimeType?.includes("text/html") && part.body?.data) {
      links.push(...extractFromHtml(decodeBody(part.body)));
    }
    if (part.mimeType?.includes("text/plain") && part.body?.data) {
      links.push(...extractFromText(decodeBody(part.body)));
    }
  }

  // dedupe by href
  const seen = new Set<string>();
  return links.filter((link) => {
    if (seen.has(link.href)) return false;
    seen.add(link.href);
    return true;
  });
}

export function extractFromText(text: string): ExtractedLink[] {
  const matches = text.matchAll(URL_REGEX);
  return Array.from(matches).map((match) => annotateLink(match[1], match[1]));
}

export function extractFromHtml(html: string): ExtractedLink[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const anchors = Array.from(doc.querySelectorAll("a[href]"));
  const links: ExtractedLink[] = anchors.map((a) => annotateLink(a.getAttribute("href") ?? "", a.textContent ?? undefined));

  // also fall back to regex for raw text nodes
  links.push(...extractFromText(doc.body.textContent ?? ""));
  return links;
}

export function annotateLink(href: string, displayText?: string): ExtractedLink {
  const flags: string[] = [];
  const domain = extractHostname(href) ?? undefined;
  if (domain) {
    flags.push(...getDomainFlags(domain));
    if (looksLikeShortener(extractETldPlusOne(domain))) {
      flags.push("url-shortener");
    }
    if (detectHomoglyph(domain)) {
      flags.push("mixed-script-domain");
    }
    if (displayText && domainMismatch(displayText, domain)) {
      flags.push("visible-domain-mismatch");
    }
    if (href.startsWith("http://") && !domain.endsWith("localhost")) {
      flags.push("insecure-link");
    }
  }
  return { href, displayText, domain, flags };
}

function domainMismatch(text: string, actualDomain: string): boolean {
  const textMatch = text.match(/[a-z0-9.-]+\.[a-z]{2,}/i);
  if (!textMatch) return false;
  const visibleDomain = textMatch[0].toLowerCase();
  const visibleBase = extractETldPlusOne(visibleDomain);
  const actualBase = extractETldPlusOne(actualDomain);
  return visibleBase !== actualBase;
}
